import {instance as axios, m3Instance} from "@/axios";
import Swal from "sweetalert2";
import { ProductFilter, ProductFilterMap, ProductList } from "@/models/ProductList";
import { Cart } from "@/models/Cart";
import {
  Login,
  ChangePassword,
  RegisterModel,
  RegisterResponse,
  ForgotPassword,
  UpdateModel,
  UpdateResponse,
  RegisterCompanyCustomerModel,
  RegisterCompanyCustomerResponse,
  UpdateCompanyCustomerModel,
  UpdateCompanyCustomerResponse
} from "@/models/Member";
import { ContactFormModel, MultiFormModel } from "@/models/Form";
import { ConsumerTokenRequest, FlowModel, KlarnaSessionRequest } from "@/models/FlowPortal";
import { Problem } from "@/models/Error";
import { Checkout } from "@/models/Checkout";
import { ExtraInfo, Payment } from "@/models/Payment";
import { CompanyModel, Customer } from "@/models/Customer";
import { Order, OrderList } from "@/models/Order";
import { AxiosResponse } from "axios";

const getFilters = async(): Promise<ProductFilter[]> => {
  try {
    return await axios.get("category/getfilters", { params: { contentId: window.contentId }});
  } catch (e) {
    return Promise.reject(e);
  }
};

const login = async (emailAddress: string, password: string): Promise<Login> => {
  try {
    return await axios.post("member/login", { emailAddress, password, contentId: window.contentId });
  } catch (e) {
    return Promise.reject(e);
  }
};
const resetPassword = async (email: string, token: string, password: string, confirmPassword: string): Promise<string> => {
  try {
    return await axios.post("member/resetpassword", { EmailAddress: email, token, password, confirmPassword, contentId: window.contentId });
  } catch (e) {
    return Promise.reject(e);
  }
};
const forgotPassword = async (emailAddress: string): Promise<string> => {
  try {
    return await axios.post("member/forgotpassword", { emailAddress, contentId: window.contentId, requestUrl: window.location.href.split('?')[0] });
  } catch (e) {
    return Promise.reject(e);
  }
};

const changePassword = async (currentPassword: string, password: string, confirmPassword: string): Promise<string> => {
  try {
    return await axios.put("member/changepassword", { currentPassword, password, confirmPassword, contentId: window.contentId });
  } catch (e) {
    return Promise.reject(e);
  }
}


export const ProductApi = {
  getProducts: async (filters: Array<string>, pageNumber: number, sort: string): Promise<ProductList> => {
    try {
      return await axios.get("category/getproducts", { params: { contentId: window.contentId, filters: JSON.stringify(filters), pageNumber, sort }});
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  getFilters:  async (): Promise<ProductFilterMap[]> => {
    try {
      const data = await getFilters();
      return data.filter(x => x.type === "parf").map(x => ({
        type: x.type,
        name: x.name,
        options: x.items.map(y => ({id: y.id, type: y.type, name: y.name}))
      }));
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    } 
  },
  isProductBulk: async (partNo: string): Promise<boolean> => {
    try {
      return await axios.get("member/isproductbulk", { params: { partNo, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  }

};
export const DataLayerApi = {
  createPurchaseEvent: async (basketid: string) => {
    try {
      const event = await axios.post("dataLayer/createPurchaseEvent", JSON.stringify(basketid), {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (event) {
        addToDataLayer(event);
      }
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail,
      });
      return Promise.reject(e);
    }
  },
}
const addToDataLayer = (data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(data);
};

export const SessionApi = {
  keepAlive: async () => {
    try {
      const response = await axios.get("session/keepalive");
      return response.data;
    } catch (e) {
      const problem = e as Problem;
      return Promise.reject(problem);

    }
  }
};
export const BasketApi = {
  getBasket: async (fetchEmpty: boolean, fetchCached: boolean): Promise<Cart> => {
    try {
      return await axios.get("basket/get", { params: { contentId: window.contentId, fetchEmpty, fetchCached }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  addToBasket: async (partNo: string, quantity: number, incrementExisting: boolean): Promise<Cart> => {
    try {
      return await axios.post("basket/addorupdate", { partNo, quantity, incrementExisting, contentId: window.contentId });
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  updateBasket: async (partNo: string, quantity: number): Promise<Cart> => {
    try {
      return await axios.put("basket/update", { partNo, quantity, contentId: window.contentId });
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  removeFromBasket: async (partNo: string): Promise<Cart> => {
    try {
      return await axios.delete("basket/remove", { params: { partNo, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  createFromOrder: async(basketId: string): Promise<Cart> => {
    try {
      return await axios.post("basket/createfromorder", null, { params: { basketId, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  createBasketFromOrder: async(basketId: string): Promise<Cart> => {
    try {
      return await axios.post("basket/createbasketfromorder", null, { params: { basketId, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  emptyAlternativeDelivery: async(): Promise<Cart> => {
    try {
      return await axios.post("basket/emptyalternativedelivery");
    }
    catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  },
  removeOrderAgainFlag: async(): Promise<Cart> => {
    try {
      return await axios.post("basket/removeorderagainflag");
    }
    catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  },
  updateBasketComment: async (comment: string): Promise<Cart> => {
    try {
      return await axios.post("basket/updatebasketcomment", null, { params: { comment }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  }
};

export const CheckoutApi = {
  getCheckout: async(): Promise<Checkout> => {
    try {
      return await axios.get("checkout/get", { params: { contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  toggleService: async(itemId: string): Promise<Checkout> => {
    try {
      return await axios.put("checkout/toggleService", null, { params: { itemId, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  setPayment: async(paymentMethodId: string): Promise<Checkout> => {
    try {
      return await axios.put("checkout/setpayment", null, { params: { paymentMethodId, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  setZipCode: async(zipCode: string): Promise<Checkout> => {
    try {
      return await axios.put("checkout/setzipcode", null, { params: { zipCode, contentId: window.contentId }}); 
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  setDelivery: async(deliveryMethodId: string): Promise<Checkout> => {
    try {
      return await axios.put("checkout/setdelivery", null, { params: { deliveryMethodId, contentId: window.contentId }}); 
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  saveOrder: async(extraInfo: ExtraInfo[], payer: Customer): Promise<Payment> => {
    try {
      return await axios.post("checkout/saveorder", { extraInfo, payer }, { params: { contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  setShipTo: async(shipTo: Customer): Promise<Checkout> => {
    try {
      return await axios.post("checkout/setshipto", shipTo);
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  }
};

export const DeliveryMethodApi = {
  isDeliveryPossible: async(productId, partNo, postCode): Promise<boolean> => {
    return await axios.get("postcodecheck/isdeliverypossible", { params: { productId: productId, partNo: partNo, postCode: postCode}})
  }
}

export const MemberApi = {
  getCustomer: async(): Promise<Customer> => {
    try {
      return await axios.get("member/getcustomer", { params: { contentId: window.contentId }});  
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  getCustomersByCompany: async(): Promise<Customer[]> => {
    try {
      return await axios.get("member/getcustomersbycompany", { params: { contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  resetpassword: async(email: string, token: string, password: string, confirmPassword: string): Promise<ChangePassword> => {
    try {
      const message = await resetPassword(email, token, password, confirmPassword);
      const reset: ChangePassword = {
        success: true,
        message: message
      };
      return reset;
    }
    catch (e) {
      const error = e as Problem;
      const change: ChangePassword = {
        success: false,
        messages: error.messages ?? error.detail?.split("|"),
      };
      return change;
    }
  },
  forgotpassword: async (emailAddress: string): Promise<ForgotPassword> => {
    try {
      const message = await forgotPassword(emailAddress);
      const forgot: ChangePassword = {
        success: true,
        message: message
      };
      return forgot;
    } catch (e) {
      const error = e as Problem;
      const member: ForgotPassword = {
        success: false,
        messages: error.messages ?? error.detail?.split("|"),
      };
      return member;
    }
  },
  setCompany: async(companyId: string): Promise<void> => {
    try {
      await axios.put("member/setcompany", { companyId, contentId: window.contentId });  
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  getFullOrderHistory: async(): Promise<Order[]> => {
    try {
      return axios.get("member/getfullorderhistory", { params: { contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  getOrderHistory: async(pageNo: number): Promise<OrderList> => {
    try {
      return axios.get("member/getorderhistory", { params: { pageNo, contentId: window.contentId }});
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  login: async (emailAddress: string, password: string): Promise<Login> => {
    try {
      const member = await login(emailAddress, password);
      member.success = true;
      return member;
    } catch (e) {
      const error = e as Problem;
      const member: Login = {
        name: "",
        success: false,
        companyType: "",
        messages: error.messages ?? error.detail?.split("|"),
      };
      return member;
    }
  },
  logout: async (): Promise<boolean> => {
    try {
      await axios.get("member/logout", { params: { contentId: window.contentId }});
      return true;
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return false
    }
  },
  register: async (registerModel: RegisterModel): Promise<RegisterResponse> => {
    try {
      //Response.data is undefined but response contains the model RegisterResponse, how to solve this?
      return await axios.post("member/register", registerModel);
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return { success: false };
    }
  },
  changePassword: async(currentPassword: string, password: string, confirmPassword: string): Promise<ChangePassword> => {
    try {
      const message = await changePassword(currentPassword, password, confirmPassword);
      const change: ChangePassword = {
        success: true,
        message: message
      };
      return change;
    }
    catch (e) {
      const error = e as Problem;
      const change: ChangePassword = {
        success: false,
        messages: error.messages ?? error.detail?.split("|"),
      };
      return change;
    }
  },
  updateCustomer: async (updateModel: UpdateModel): Promise<UpdateResponse> => {
    try {
      const message = await axios.post("member/updatecustomer", updateModel);
      const update: UpdateResponse = {
        success: true,
        message: message
      };
      return update;
    } catch (e) {
      const error = e as Problem;
      const update: UpdateResponse = {
        success: false,
        messages: error.messages ?? error.detail?.split("|")
      };
      return update;
    }
  },
  saveBasket: async(): Promise<string> => {
    try {
      return await axios.post("member/saveBasketId");
    }
    catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return Promise.reject(e);
    }
  },
  registerCompanyCustomer: async (registerCompanyCustomerModel: RegisterCompanyCustomerModel): Promise<RegisterCompanyCustomerResponse> => {
    try {
      return await axios.post("member/registercompanycustomer", registerCompanyCustomerModel);
    } catch (e) {
	  const problem = e as Problem;
  	  await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
	  return { success: false };
	}
  },
  updateCompanyCustomer: async (updateCompanyCustomerModel: UpdateCompanyCustomerModel): Promise<UpdateCompanyCustomerResponse> => {
    try {
      return await axios.post("member/updatecompanycustomer", updateCompanyCustomerModel);
    } catch (e) {
      const error = e as Problem;
      return { success: false };
    }
  },
  registerFlowPortalCustomer: async (flowModel: FlowModel): Promise<RegisterResponse> => {
    try {

      const response = await m3Instance.post<RegisterResponse>('member/register', {
        ...flowModel,
        activationCode: flowModel.activationCode,
      });
      return response.data;
    } catch (e) {
      const problem = e as Problem;
      await Swal.fire<void>({
        icon: "error",
        text: problem.detail
      });
      return { success: false };
    }
  },
  createKlarnaSession: async (sessionRequest : KlarnaSessionRequest): Promise<void> => {
    try {
      return await m3Instance.post("agreement/createklarnasession", JSON.stringify(sessionRequest), {
        headers: {
          'Content-Type': 'application/json'
        }
      })
    } catch (e) {
      const error = e as Problem;
    }
  },
  generateConsumerToken: async (request: ConsumerTokenRequest): Promise<boolean> => {
    try {
      await m3Instance.post("agreement/generateconsumertoken", JSON.stringify(request),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      return true;
    } catch (e) {
      const error = e as Problem;
      return false;
    }
  }
};

export const FormApi = {
  sendContactForm: async(contactForm: ContactFormModel): Promise<boolean> => {
    try {
      return await axios.post("form/sendcontactform", contactForm);
    } catch (e) {
      return false;
    }
  },
  sendMultiForm: async(multiForm: MultiFormModel): Promise<boolean> => {
    try {
      return await axios.post("form/sendmultiform", multiForm);
    } catch (e) {
      return false;
    }
  }
}